import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 사내 투표 — 웹과 앱이 <b>같은 파일</b>을 쓴다.
 *
 * `front/src/lib` 아래에 둔 이유가 그것이다(`mobile/tsconfig.json` 의 `@/lib/*` 별칭). 홈 모달이
 * 두 곳에 서는데 판정을 두 벌 적으면, 한쪽만 고친 날 앱에서는 이미 한 투표가 계속 뜬다.
 *
 * <h2>「했는지」 를 화면이 판정하지 않는다</h2>
 * 서버가 <b>아직 안 한 것만</b> 내려준다({@code /polls/pending}). 화면이 목록을 받아 걸러 내는
 * 구조였다면, 그 조건을 웹·앱·위젯이 각자 흉내 내게 된다.
 *
 * <h2>집계는 마감 전에 «오지 않는다»</h2>
 * {@code voteCount} 가 `null` 이면 아직 못 보는 것이고, `0` 이면 정말 아무도 안 고른 것이다.
 * 화면이 가리는 것이 아니라 응답에 담기지 않는다 — 가리기만 했다면 개발자 도구로 그만이다.
 */

export type PollStatus = 'DRAFT' | 'OPEN' | 'CLOSED';

export type PollOptionView = {
  id: string;
  label: string;
  /** 마감 전에는 `null`. 0 과 구별해야 한다 — 「아무도 안 골랐다」 가 아니라 「아직 못 본다」다. */
  voteCount: number | null;
  /**
   * 눌러 볼 수 있는 선택지인가 — 홈페이지 시안 id (V350).
   *
   * 값이 있으면 투표 창에 「눌러보기」 가 서고, 누르면 그 시안이 <b>전체 화면</b>으로 열려
   * 메뉴까지 눌러 볼 수 있다({@code lab/homepage/HomepagePreview}). 글로 적힌 이름을 보고
   * 고르는 것과 홈페이지를 굴려 보고 고르는 것은 다른 일이다.
   *
   * <p>앱에는 이 시안을 띄울 코드가 없어, 이 값이 붙은 투표는 <b>서버가 앱에 안 내려보낸다</b>.
   */
  previewKey: string | null;
  /**
   * 이 선택지를 고른 사람에게 여는 <b>의견 칸</b>의 안내 문구 (V353). `null` 이면 칸이 없다.
   *
   * <p>「직접 적기」 와 다르다 — 그쪽은 고르는 <b>대신</b> 적는 한 표이고, 이쪽은 <b>골라 놓고</b>
   * 덧붙이는 말이다. 비워 둬도 투표된다: 「다섯 다 아니다」 까지만 분명한 사람의 표가 갈 곳이
   * 있어야 한다.
   */
  noteLabel: string | null;
  /** 덧붙여 적힌 말들. <b>마감 전에는 빈 목록</b>이다 — 화면이 가리는 것이 아니라 오지 않는다. */
  notes: string[];
};

/** 직접 적은 안 — 같은 문구끼리 묶인 채로 온다. 마감 전에는 <b>빈 목록</b>이다. */
export type PollWriteInView = {
  text: string;
  voteCount: number;
};

export type PollView = {
  id: string;
  title: string;
  /** 왜 묻는지. 적지 않았으면 `null` — 그때 화면은 아무것도 그리지 않는다. */
  description: string | null;
  status: PollStatus;
  /** ISO 8601. 비어 있으면 「받기 시작」 을 누른 때부터다. */
  opensAt: string | null;
  /** ISO 8601. 비어 있으면 마감이 없고, 사람이 닫아야 결과가 열린다. */
  closesAt: string | null;
  /** 「직접 적기」 칸을 여는가. 꺼져 있으면 서버도 자유 서술을 받지 않는다. */
  allowWriteIn: boolean;
  voted: boolean;
  resultsOpen: boolean;
  participantCount: number;
  /**
   * 이 인원이 참여하면 마감된다 (V350). `null` 이면 인원으로는 마감하지 않는다.
   *
   * <p>마감 시각과 다른 축이다 — 「금요일까지」 는 기다리게 하지만 이쪽은 <b>다 물어본 순간</b>
   * 끝난다. 마지막 표가 그 자리에서 닫으므로 배치를 기다리지 않는다.
   */
  closeAtCount: number | null;
  options: PollOptionView[];
  writeIns: PollWriteInView[];
  /** 대상 소속. <b>비어 있으면 전 직원</b>이다(V212). */
  scopes: PollScopeView[];
};

/** 투표 대상 소속 하나 — 부서 또는 현장. 이름으로 잡는다(계정 소속·배치표가 이름을 들고 있다). */
export type PollScopeView = {
  type: 'DEPARTMENT' | 'SITE';
  name: string;
};

export type PollUpsertInput = {
  title: string;
  description: string | null;
  opensAt: string | null;
  closesAt: string | null;
  allowWriteIn: boolean;
  /** 이 인원이 참여하면 마감한다. 비우면 인원으로는 마감하지 않는다. */
  closeAtCount: number | null;
  /**
   * 선택지.
   *
   * `null` 은 <b>건드리지 않는다</b>는 뜻이고 `[]` 는 <b>다 지운다</b>는 뜻이다. 표가 들어온
   * 뒤에는 어느 쪽이든 서버가 거절하므로(409 `POLL_HAS_BALLOTS`), 그때는 `null` 로 보낸다 —
   * 제목·마감만 고치려다 「선택지를 바꿀 수 없다」 로 막히지 않게.
   *
   * <p>문구만이 아니라 `previewKey` 까지 <b>받은 그대로 돌려보낸다</b>(V350). 서버는 선택지를
   * 지우고 다시 넣으므로, 문구만 보내면 눌러 볼 수 있던 선택지가 저장 한 번에 평범해진다.
   */
  options: Array<{ label: string; previewKey: string | null; noteLabel: string | null }> | null;
  /** 대상 소속. 비우면 전 직원이다. */
  scopes: PollScopeView[];
};

/**
 * 손을 뗀 투표 — <b>「나중에」 로 미룬 것</b>과 <b>방금 던진 것</b>.
 *
 * <h2>둘을 가른다 (사용자 지적 2026-09-02)</h2>
 * 한 서랍에 같은 이름으로 넣어 뒀더니, 홈에서 던지고 투표 탭으로 갔을 때 <b>「투표하기」 가
 * 그대로 서 있었다</b>. 서버 목록이 도착하기 전까지 화면은 투표 전에 받아 둔 목록을 그리는데,
 * 게시판은 이 표시를 보고도 「미룬 것인지 던진 것인지」 를 알 수 없어 쓸 수가 없었다.
 *
 * <p>이제 던진 것은 {@code voted:<id>} 로 들어간다. 홈 창은 둘 다 보고 물러서고
 * ({@link isPollDismissed}), 게시판은 <b>던진 것만</b> 본다({@link hasVotedLocally}) — 미룬
 * 사람에게는 목록에서 「투표하기」 가 그대로 서 있어야 한다.
 *
 * <h2>왜 서버에 남기지 않나</h2>
 * 미룬 것을 표에 담으면 <b>누가 언제 미뤘는지</b>가 쌓인다. 그 기록은 투표를 독려하는 데 쓰이게
 * 되고, 그 순간 「비밀투표인데 안 한 사람은 지목된다」 가 된다. 던진 것은 서버가 이미 알고 있어
 * ({@code voted}) 여기 남는 것은 <b>응답이 오기까지의 몇백 밀리초</b>를 메우는 표시일 뿐이다.
 *
 * <p>{@code sessionStorage} 가 있으면 그것을 쓰고(웹: 새로고침해도 다시 안 뜬다), 없으면
 * 메모리에 둔다(앱: 앱을 다시 켜면 다시 뜬다). 어느 쪽이든 <b>다시 로그인하면 다시 묻는다</b> —
 * 미룬 사람에게 영영 안 묻는 것은 안 한 것과 같다.
 */
const DISMISS_KEY = 'bomi.polls.dismissed';
const dismissedInMemory = new Set<string>();

function dismissedStore(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // 브라우저가 저장소를 막아 둔 경우(사생활 보호 모드). 메모리로 물러선다.
    return null;
  }
}

export function dismissPoll(pollId: string): void {
  dismissedInMemory.add(pollId);
  const store = dismissedStore();
  if (!store) return;
  const kept = new Set<string>(JSON.parse(store.getItem(DISMISS_KEY) ?? '[]') as string[]);
  kept.add(pollId);
  store.setItem(DISMISS_KEY, JSON.stringify([...kept]));
}

/** 방금 던졌다. 서버가 알려 주기 전까지 화면이 대신 아는 표시다. */
export function markVoted(pollId: string): void {
  dismissPoll(votedKey(pollId));
}

/** 이 판에서 내가 던졌는가 — <b>미룬 것은 세지 않는다</b>. 게시판의 「투표하기」 가 이것만 본다. */
export function hasVotedLocally(pollId: string): boolean {
  return isDismissed(votedKey(pollId));
}

/** 홈 창이 물러설 이유 — 던졌거나 미뤘거나. */
export function isPollDismissed(pollId: string): boolean {
  return isDismissed(pollId) || hasVotedLocally(pollId);
}

function votedKey(pollId: string) {
  return `voted:${pollId}`;
}

function isDismissed(key: string): boolean {
  if (dismissedInMemory.has(key)) return true;
  const store = dismissedStore();
  if (!store) return false;
  try {
    return (JSON.parse(store.getItem(DISMISS_KEY) ?? '[]') as string[]).includes(key);
  } catch {
    return false;
  }
}

/**
 * 지금 나에게 남은 투표.
 *
 * <p>`staleTime` 을 길게 두지 않는다 — 관리자가 투표를 연 직후에 홈을 열어도 떠야 한다. 대신
 * 홈에서만 부르므로 잦아지지 않는다.
 */
export function usePendingPolls() {
  return useQuery({
    queryKey: queryKeys.pollsPending,
    queryFn: () => apiFetch<PollView[]>('/v1/polls/pending'),
    staleTime: 30_000,
  });
}

/**
 * 마감했고 내가 참여한 투표.
 *
 * <p>결과를 <b>실제로 공개하는</b> 자리다. 서버가 참여 여부로 이미 걸러 주므로 화면은 받은 것을
 * 그대로 띄운다 — 「나는 이 투표를 했던가」 를 화면이 기억할 필요가 없다.
 */
export function useClosedPollResults() {
  return useQuery({
    queryKey: queryKeys.pollResults,
    queryFn: () => apiFetch<PollView[]>('/v1/polls/results'),
    staleTime: 5 * 60_000,
  });
}

/**
 * 한 표.
 *
 * <p>선택지를 고르거나 직접 적거나 <b>둘 중 하나</b>다 — 한 표가 두 곳에 세어지는 순간
 * 「한 사람 한 표」 가 깨진다.
 *
 * <p>둘이 함께 가는 길이 하나 있다(V353): {@code noteLabel} 이 달린 칸을 고르고 <b>덧붙인
 * 말</b>을 적은 경우다. 그때 글은 다른 표가 아니라 그 표에 얹힌 것이라 세지 않는다.
 */
export function useCastVote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pollId,
      optionId,
      writeIn,
    }: {
      pollId: string;
      optionId?: string | null;
      /**
       * 고른 칸이 없으면 <b>고르는 대신 적은 한 표</b>, 있으면 그 칸에 <b>덧붙인 말</b>이다
       * (V353). 뒤쪽은 세지 않는다 — 표는 이미 그 칸에 들어가 있다.
       */
      writeIn?: string | null;
    }) =>
      apiFetch<PollView>(`/v1/polls/${pollId}/vote`, {
        method: 'POST',
        body: { optionId: optionId ?? null, writeIn: writeIn ?? null },
      }),
    onSuccess: () => {
      // 던진 투표는 목록에서 사라져야 한다 — 안 그러면 창을 닫는 순간 같은 모달이 다시 선다.
      void queryClient.invalidateQueries({ queryKey: queryKeys.pollsPending });
      void queryClient.invalidateQueries({ queryKey: queryKeys.polls });
      // TO DO 위젯의 「아직 참여하지 않으셨습니다」 줄도 함께 사라져야 한다. 그 줄은 서버가
      // 같은 판정으로 만들지만(`TodoService.pollTodos`), 캐시는 따로 들고 있다.
      void queryClient.invalidateQueries({ queryKey: queryKeys.todoItems });
    },
  });
}

// ── 목록·만들기(보미소식 > 투표) ─────────────────────────────────────────────────

export function usePolls() {
  return useQuery({
    queryKey: queryKeys.polls,
    queryFn: () => apiFetch<PollView[]>('/v1/news/polls'),
  });
}

/** 만들고·고치고·여닫고·지우는 것 한 묶음. 어느 것이 끝나든 목록과 홈 모달을 함께 갱신한다. */
export function usePollActions() {
  const queryClient = useQueryClient();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.polls });
    void queryClient.invalidateQueries({ queryKey: queryKeys.pollsPending });
  };

  const create = useMutation({
    mutationFn: (input: PollUpsertInput) =>
      apiFetch<PollView>('/v1/news/polls', { method: 'POST', body: input }),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: ({ id, ...input }: PollUpsertInput & { id: string }) =>
      apiFetch<PollView>(`/v1/news/polls/${id}`, { method: 'PUT', body: input }),
    onSuccess: refresh,
  });

  const open = useMutation({
    mutationFn: (id: string) => apiFetch<PollView>(`/v1/news/polls/${id}/open`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const close = useMutation({
    mutationFn: (id: string) => apiFetch<PollView>(`/v1/news/polls/${id}/close`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/news/polls/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  return { create, update, open, close, remove };
}
